import { useTranslate } from '@refinedev/core';
import {
  Descriptions,
  Flex,
  Progress,
  Space,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import type { DescriptionsProps } from 'antd';
import type { AudioPositionOut } from '@/api/generated/schemas';
import { orDash, useVerticalDescProps } from '@/components/common/show/shared';
import { ShowIconLink } from '@/components/common/ShowIconLink';
import {
  ProductFormatTag,
  ProductVariantTag,
} from '@/components/progress/tags';
import { formatDateTime } from '@/utils/format';
import { PublicationDrawerIcon } from '@/components/user_catalog/publications/drawer/PublicationDrawerIcon';
import { PublicationInfoPopover } from '@/components/user_catalog/publications/PublicationInfoPopover';

type Props = {
  /** Audio and ebook positions share this shape. */
  position: AudioPositionOut;
};

/** One LWW resume position behind a progression, with its raw locator. */
export const ProgressionPositionPanel = ({ position }: Props) => {
  const t = useTranslate();
  const verticalDescProps = useVerticalDescProps();
  const { token } = theme.useToken();

  const field = (name: string) =>
    t(`progress.progressions.position_fields.${name}`);

  const items: DescriptionsProps['items'] = [
    {
      key: 'id',
      label: field('id'),
      children: orDash(
        position.id != null && (
          <ShowIconLink resource="progress.positions" id={position.id} />
        )
      ),
    },
    {
      key: 'publisher_id',
      label: field('publisher_id'),
      children: orDash(position.publisher_id),
    },
    {
      key: 'account_id',
      label: field('account_id'),
      children: orDash(position.account_id),
    },
    {
      key: 'profile_id',
      label: field('profile_id'),
      children: orDash(position.profile_id),
    },
    {
      key: 'publication_id',
      label: field('publication_id'),
      children: orDash(
        position.publication_id != null && (
          <PublicationInfoPopover publicationId={position.publication_id} />
        )
      ),
    },
    {
      key: 'product_id',
      label: field('product_id'),
      children: orDash(
        position.product_id != null && (
          <Space size={4}>
            {position.product_id}
            {position.publication_id != null && (
              <PublicationDrawerIcon publicationId={position.publication_id} />
            )}
          </Space>
        )
      ),
    },
    {
      key: 'product_version',
      label: field('product_version'),
      children: orDash(position.product_version),
    },
    {
      key: 'product_kind',
      label: field('product_kind'),
      children: orDash(
        position.product_kind && (
          <ProductFormatTag
            value={position.product_kind}
            labels="progressions"
          />
        )
      ),
    },
    {
      key: 'product_variant',
      label: field('product_variant'),
      children: orDash(
        position.product_variant && (
          <ProductVariantTag
            value={position.product_variant}
            labels="progressions"
          />
        )
      ),
    },
    {
      key: 'device_id',
      label: field('device_id'),
      children: orDash(
        position.device_id && (
          <Typography.Text code>{position.device_id}</Typography.Text>
        )
      ),
    },
    {
      key: 'total_progression',
      label: field('total_progression'),
      span: { xs: 1, sm: 2 },
      children: orDash(
        position.total_progression != null && (
          // Positions carry a 0..1 fraction; the raw value stays on hover.
          <Tooltip title={position.total_progression.toFixed(8)}>
            <Progress
              percent={Math.round(position.total_progression * 100)}
              style={{ marginBottom: 0 }}
            />
          </Tooltip>
        )
      ),
    },
    {
      key: 'client_timestamp',
      label: field('client_timestamp'),
      children: orDash(formatDateTime(position.client_timestamp)),
    },
    {
      key: 'synced_at',
      label: field('synced_at'),
      children: orDash(formatDateTime(position.synced_at)),
    },
  ];

  return (
    <Flex vertical gap={token.margin}>
      <Descriptions
        {...verticalDescProps}
        column={{ xs: 1, sm: 2 }}
        items={items}
      />

      <div>
        <Typography.Text
          type="secondary"
          style={{ fontSize: token.fontSizeSM }}
        >
          {field('locator')}
        </Typography.Text>
        {/* Opaque JSONB whose shape differs per product kind — shown raw,
            scrolling inside the card rather than stretching the layout. */}
        <pre
          style={{
            margin: 0,
            maxHeight: 280,
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontSize: token.fontSizeSM,
          }}
        >
          {JSON.stringify(position.locator, null, 2)}
        </pre>
      </div>
    </Flex>
  );
};
